import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Image as ImageIcon, PenLine, FileWarning, Variable, Minus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const BRAND_BLOCKS = {
    logo: `<p align="center"><img src="/assets/nexusone-logo.png" width="150"/></p><p align="center"><span style="font-size:9pt;color:#64748B">Development Planning · City of Brampton</span></p><p> </p>`,
    signature: `<p> </p><p><i>Yours truly,</i></p><p><b>&lt;&lt;planner&gt;&gt;</b><br/><span style="font-size:9pt;color:#334155">Development Planning · City of Brampton<br/>planning@brampton.ca</span></p>`,
    disclaimer: `<p> </p><p><span style="font-size:7.5pt;color:#94A3B8">This letter was generated by LandLogic NexusOne on behalf of the City of Brampton. The information contained herein is confidential and intended solely for the addressee. E.&amp;O.E.</span></p>`,
};

const PLACEHOLDERS = ["<<gendate>>", "<<appreference>>", "<<apptitle>>", "<<apptype>>", "<<appaddress>>", "<<appward>>", "<<applicant>>", "<<planner>>", "<<appstatus>>", "<<appdesc>>"];

export default function RichTextEditor({ value, onChange, testid, minRows = 10 }) {
    const { tr } = useLanguage();
    const ref = useRef(null);
    const lastEmitted = useRef(null);

    useEffect(() => {
        if (ref.current && value !== lastEmitted.current) {
            ref.current.innerHTML = value || "";
            lastEmitted.current = value;
        }
    }, [value]);

    const emit = () => {
        const html = ref.current?.innerHTML || "";
        lastEmitted.current = html;
        onChange(html);
    };

    const exec = (cmd, arg = null) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };
    const insert = (html) => { ref.current?.focus(); document.execCommand("insertHTML", false, html); emit(); };

    const Btn = ({ icon: Icon, title, onClick, testid: tid }) => (
        <button type="button" data-testid={tid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
            className="p-1.5 rounded-[6px] text-slate-500 hover:bg-[#00B4D8]/10 hover:text-[#0092B0] transition-colors">
            <Icon className="w-3.5 h-3.5" />
        </button>
    );

    return (
        <div className="rounded-[10px] border border-slate-200 dark:border-white/10 bg-background overflow-hidden focus-within:ring-2 focus-within:ring-[#00B4D8]/40" data-testid={testid}>
            <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-slate-100 dark:border-white/5 bg-slate-50/70 dark:bg-white/5">
                <Btn icon={Bold} title={tr("Bold")} onClick={() => exec("bold")} testid="rte-bold" />
                <Btn icon={Italic} title={tr("Italic")} onClick={() => exec("italic")} testid="rte-italic" />
                <Btn icon={Underline} title={tr("Underline")} onClick={() => exec("underline")} testid="rte-underline" />
                <Btn icon={List} title={tr("Bullet list")} onClick={() => exec("insertUnorderedList")} testid="rte-bullets" />
                <Btn icon={ListOrdered} title={tr("Numbered list")} onClick={() => exec("insertOrderedList")} testid="rte-numbers" />
                <Btn icon={Minus} title={tr("Divider")} onClick={() => insert("<hr/><p> </p>")} testid="rte-hr" />
                <span className="w-px h-4 bg-slate-200 dark:bg-white/10 mx-1" />
                <select data-testid="rte-placeholder" defaultValue=""
                    onChange={(e) => { if (e.target.value) { insert(e.target.value); e.target.value = ""; } }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="text-[11px] font-semibold text-slate-500 bg-transparent border border-slate-200 dark:border-white/10 rounded-[6px] px-1.5 py-1 focus:outline-none">
                    <option value="" disabled>{tr("Insert placeholder…")}</option>
                    {PLACEHOLDERS.map((p) => <option key={p} value={p.replace(/</g, "&lt;").replace(/>/g, "&gt;")}>{p}</option>)}
                </select>
                <span className="w-px h-4 bg-slate-200 dark:bg-white/10 mx-1" />
                <Btn icon={ImageIcon} title={tr("Insert logo / brand header")} onClick={() => insert(BRAND_BLOCKS.logo)} testid="rte-brand-logo" />
                <Btn icon={PenLine} title={tr("Insert signature block")} onClick={() => insert(BRAND_BLOCKS.signature)} testid="rte-brand-signature" />
                <Btn icon={FileWarning} title={tr("Insert disclaimer")} onClick={() => insert(BRAND_BLOCKS.disclaimer)} testid="rte-brand-disclaimer" />
            </div>
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                data-testid={`${testid}-area`}
                className="mx-auto my-4 bg-white text-slate-800 rounded-[6px] shadow-[0_8px_30px_-10px_rgba(10,37,64,0.25)] px-10 py-8 text-[11pt] leading-[1.65] focus:outline-none [&_img]:max-w-[150px] [&_img]:mx-auto [&_hr]:border-slate-200 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                style={{ minHeight: `${minRows * 24}px`, maxWidth: 640 }}
            />
        </div>
    );
}
